import { useSelector } from 'react-redux';
import type { AuthState } from './types';

type RootWithAuth = { auth: AuthState };

/**
 * Returns true when the current user's role matches any of the allowed roles.
 * Usage: `const canExport = useHasRole(['admin', 'manager']);`
 *
 * Matching is **case-insensitive** — the backend seeds role names in
 * mixed case (`SuperAdmin`, `Administrator`, `Manager`, `Cashier`,
 * `Accountant`, `TenantAdmin`, `Owner`, …), while callers throughout
 * this codebase use lowercase shorthand (`['admin', 'manager', 'staff']`).
 * A case-sensitive `.includes()` therefore silently blocked every admin
 * from every analytics dashboard (they all showed "Access denied").
 *
 * Admin bypass: an EXACT-MATCH allowlist of seeded admin-flavour role
 * names (Administrator, SuperAdmin, TenantAdmin, Owner) is treated as
 * universally permitted. Substring matches (`role.includes('admin')`)
 * are deliberately avoided so a future tenant-defined role like
 * `NonAdmin` or `AdminAssistant` doesn't silently gain full access.
 * `useHasPermission` uses the same allowlist for parity.
 */
export const ADMIN_ROLE_NAMES = new Set([
  'administrator',
  'superadmin',
  'tenantadmin',
  'owner',
]);

export function useHasRole(allowed: string | string[]): boolean {
  const role = useSelector((s: RootWithAuth) => s.auth.user?.role);
  if (!role) return false;

  const roleLc = String(role).trim().toLowerCase();

  if (ADMIN_ROLE_NAMES.has(roleLc)) return true;

  const list = (Array.isArray(allowed) ? allowed : [allowed]).map((r) =>
    String(r).trim().toLowerCase(),
  );
  return list.includes(roleLc);
}
